/**
 * Angular Ticket Type Selector Component
 *
 * Migrated version of the AngularJS acme-ticket-type-selector component.
 * Original: apps/b2b-ui/common/components/ticket-type-selector/ticket-type-selector.component.ts
 *
 * A "Add Ticket Type" dropdown used in the order edit view: a button (with a
 * count-aware label) that opens a list of remaining ticket types for an event,
 * each showing name, description and price (with discounted price when
 * different). Selecting an item emits `select` with `{ selectedItem }` and
 * clears the internal selection so the same option can be chosen again.
 *
 * The legacy `combobox` typeahead is replaced here by the shared
 * `acme-combobox` component: this controller stays thin and only owns the
 * ticket-type-specific data shape (Inputs/Outputs, computedLabel/uniqueName,
 * hasDiscount) plus the per-row template projected into the combobox.
 *
 * Usage in Angular templates:
 *   <app-ticket-type-selector
 *     [items]="event.morePrices"
 *     [eventId]="event.eventId"
 *     (select)="addTicketType($event.selectedItem)">
 *   </app-ticket-type-selector>
 *
 * Usage in AngularJS templates (after downgrade — element name preserved):
 *   <acme-ticket-type-selector
 *     event-id="event.eventId"
 *     [items]="event.$meta.morePrices"
 *     on-select="ordersEdit.addTicketTypeForEvent($event.selectedItem, event)">
 *   </acme-ticket-type-selector>
 */
import { Component, EventEmitter, HostBinding, Input, Output, computed, signal } from '@angular/core';
import { CurrencyPipe, NgClass, NgIf } from '@angular/common';

import { ComboboxComponent } from '../combobox/combobox.component';
import type { TODO } from '@b2b/types/core';

/**
 * Represents a ticket type item that can be selected.
 * Based on the structure returned by getRemainingPricesForEvent.
 */
export interface TicketTypeItem {
  name: string;
  ticketingTypeName?: string;
  ticketingTypeDescription?: string;
  price: string | number | null;
  discountedPrice?: string | number | null;
  personType?: {
    id?: string;
    name?: string;
    description?: string;
  };
  csrDisabled?: boolean;
  salesChannels?: TODO[];
}

export interface TicketTypeSelectorSelectEvent {
  selectedItem: TicketTypeItem;
}

@Component({
  selector: 'app-ticket-type-selector',
  standalone: true,
  imports: [NgIf, NgClass, CurrencyPipe, ComboboxComponent],
  template: require('./ticket-type-selector.component.html'),
  styles: [require('./ticket-type-selector.component.less')],
})
export class TicketTypeSelectorComponent {
  private readonly _items = signal<TicketTypeItem[]>([]);
  @Input()
  get items(): TicketTypeItem[] { return this._items(); }
  set items(value: TicketTypeItem[]) { this._items.set(value); }

  private readonly _eventId = signal('');
  @Input()
  get eventId(): string { return this._eventId(); }
  set eventId(value: string) { this._eventId.set(value); }

  private readonly _label = signal('');
  @Input()
  get label(): string { return this._label(); }
  set label(value: string) { this._label.set(value); }

  @Input() disabled = false;
  /** When true (default), typing in the input filters items by `name` (case-insensitive). */
  @Input() filterable = true;

  @Output() select = new EventEmitter<TicketTypeSelectorSelectEvent>();

  @HostBinding('class.acme-ticket-type-selector') readonly baseClass = true;

  readonly computedLabel = computed(() => {
    if (this._label()) return this._label();
    const count = this._items()?.length ?? 0;
    return count > 0 ? `Add Ticket Type (${count} more)` : 'Add Ticket Type';
  });

  readonly uniqueName = computed(() => `ticket-type-selector-${this._eventId()}`);

  hasDiscount(item: TicketTypeItem): boolean {
    return item.price !== item.discountedPrice;
  }

  onSelectionChange(item: TicketTypeItem): void {
    this.select.emit({ selectedItem: item });
  }
}
